/** Insets of a view in px: top, right, bottom, left. */
export type ViewInsets = [number, number, number, number];

interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The insets that keep the free part of a view clear of overlays docked to its edges (the minimap
 * in a corner, the dock at the bottom). Each overlay gives up the band along the edge that costs the
 * view the least area: a corner overlay takes the narrower strip, a bottom dock the bottom band.
 * Overlays outside the view or without area are ignored.
 */
export function overlayInsets(
  view: Rect,
  overlays: readonly Rect[]
): ViewInsets {
  const width = view.right - view.left;
  const height = view.bottom - view.top;
  const insets: ViewInsets = [0, 0, 0, 0];
  for (const overlay of overlays) {
    const left = Math.max(overlay.left, view.left);
    const right = Math.min(overlay.right, view.right);
    const top = Math.max(overlay.top, view.top);
    const bottom = Math.min(overlay.bottom, view.bottom);
    if (right <= left || bottom <= top) continue;

    const bands: ViewInsets = [
      bottom - view.top,
      view.right - left,
      view.bottom - top,
      right - view.left,
    ];
    const losses = [
      bands[0] * width,
      bands[1] * height,
      bands[2] * width,
      bands[3] * height,
    ];
    const side = losses.indexOf(Math.min(...losses));
    insets[side] = Math.max(insets[side], bands[side]);
  }
  return insets;
}
